import React from "react";
import { StyledTable, EmptyContainer, Empty } from "./Style";
import { TableComponentProps } from "./types";

/**
 * Common Table Component
 * A reusable table component built on Ant Design Table
 * 
 * Supports both JavaScript and TypeScript usage
 * 
 * @template T - The type of data in the table
 * 
 * @example
 * ```tsx
 * <Table
 *   columns={columns}
 *   dataSource={data}
 *   rowKey="id"
 *   loading={false}
 *   pagination={false}
 *   emptyText="No data"
 * />
 * ```
 */
const Table = <T extends Record<string, any> = any>({
  columns,
  dataSource,
  rowKey = "id",
  loading = false,
  pagination = false,
  emptyText = "No data",
  emptyDescription,
  scroll = {x: true, y: 600},
  locale,
  ...rest
}: TableComponentProps<T>) => {
  // Default empty state
  const defaultEmptyText = (
    <EmptyContainer>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyDescription || emptyText}
      />
    </EmptyContainer>
  );

  const tableLocale = locale || {
    emptyText: defaultEmptyText,
  };

  // Prepare all props for StyledTable
  const styledTableProps = {
    columns,
    dataSource,
    rowKey,
    loading,
    pagination,
    scroll,
    locale: tableLocale,
    theme: rest.theme,
    ...rest,
  };

  return (
    <StyledTable
      {...(styledTableProps as any)}
    />
  );
};

export default Table;

